import { type MutableRefObject, useCallback, useMemo } from "react";
import { Flex, Text } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton, useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { RequiredFieldsMissingBanner } from "../../../DocumentManagement/DocumentDetailsRightSidebar/RequiredFieldsMissingBanner";
import {
  AddressValues,
  type FormSectionConfig,
  type TaxFormFieldValue,
  type TaxFormRendererProps,
  type TaxFormValues,
} from "../../types";
import { FormSection } from "../FormSection";
import * as styles from "./styles_vanilla.css";

const isAddressComplete = (value: AddressValues): boolean => {
  return Boolean(value && value.country && value.streetLine1 && value.city && value.zipCode);
};

type RequiredFieldsCounterProps = {
  sections: FormSectionConfig[];
};

const RequiredFieldsCounter = ({ sections }: RequiredFieldsCounterProps) => {
  const { values } = useFormContext<TaxFormValues>();

  const getNestedValue = (obj: TaxFormValues, path: string): TaxFormFieldValue => {
    return path.split(".").reduce<TaxFormFieldValue>((acc, key) => {
      if (acc && typeof acc === "object" && !(acc instanceof Date)) {
        return (acc as Record<string, TaxFormFieldValue>)[key];
      }
      return undefined;
    }, obj as TaxFormFieldValue);
  };

  const { completedCount, totalCount } = useMemo(() => {
    let completed = 0;
    let total = 0;

    for (const section of sections) {
      if (section.showWhen && !section.showWhen(values)) continue;

      for (const field of section.fields) {
        if (!field.required) continue;
        if (field.type === "info") continue;
        if (field.showWhen && !field.showWhen(values)) continue;

        // For label fields with linkedField, check the linked field's completion
        if (field.type === "label" && field.linkedField) {
          total++;
          const linkedValue = getNestedValue(values, field.linkedField);
          if (isAddressComplete(linkedValue as AddressValues)) completed++;
          continue;
        }

        // Skip label fields without linkedField
        if (field.type === "label") continue;

        total++;
        const value = getNestedValue(values, field.name);
        let isCompleted = false;
        if (field.type === "checkbox") {
          isCompleted = value === true;
        } else if (field.type === "taxResidency") {
          isCompleted = Array.isArray(value) && value.length > 0;
        } else {
          isCompleted = value !== undefined && value !== null && value !== "";
        }
        if (isCompleted) completed++;
      }
    }

    return { completedCount: completed, totalCount: total };
  }, [sections, values]);

  return (
    <Flex flexDirection="row" gap="2px">
      <Text className={styles.requiredFieldsCounterAsterisk}>*</Text>
      <Text variant="secondary-sm">
        Required fields ({completedCount} of {totalCount} completed)
      </Text>
    </Flex>
  );
};

type FormFooterProps = {
  onCancel: (isDirty: boolean) => void;
  dirtyRef?: MutableRefObject<boolean>;
  submitButtonText: string;
  cancelButtonText: string;
};

const FormFooter = ({
  onCancel,
  dirtyRef,
  submitButtonText,
  cancelButtonText,
}: FormFooterProps) => {
  const { dirty } = useFormContext<TaxFormValues>();

  // Write the dirty signal synchronously during render so consumers reading
  // it from event handlers (e.g. navigation/close handlers) never observe a
  // stale value between an edit and the next commit.
  if (dirtyRef) {
    dirtyRef.current = dirty;
  }

  const handleCancel = useCallback(() => {
    onCancel(dirty);
  }, [dirty, onCancel]);

  return (
    <Flex className={styles.footerButtons}>
      <CancelButton onClick={handleCancel}>{cancelButtonText}</CancelButton>
      <SubmitButton disabled={!dirty}>{submitButtonText}</SubmitButton>
    </Flex>
  );
};

export const TaxFormRenderer = ({
  config,
  onSubmit,
  onCancel,
  dirtyRef,
  submitButtonText = "Save",
  cancelButtonText = "Cancel",
  initialValues,
}: TaxFormRendererProps) => {
  const mergedInitialValues = useMemo(
    () => ({ ...config.initialValues, ...initialValues }),
    [config.initialValues, initialValues],
  );

  const handleSubmit = useCallback(
    async (values: TaxFormValues) => {
      await onSubmit(values);
    },
    [onSubmit],
  );

  const renderSection = (section: FormSectionConfig, index: number) => (
    <FormSection
      key={section.id}
      section={section}
      header={index === 0 ? { title: config.title, subtitle: config.subtitle } : undefined}
    />
  );

  return (
    <Form<TaxFormValues>
      initialValues={mergedInitialValues}
      yupValidationSchema={config.validationSchema}
      onSubmit={handleSubmit}
      submitOnlyChangedValues={false}
    >
      <Flex justifyContent="center" flexDirection="column" gap={Sizes.LG}>
        <RequiredFieldsMissingBanner />
        {/* Section Cards - first section includes header */}
        {config.sections.map((section, index) => renderSection(section, index))}

        {/* Footer - no card wrapper */}
        <Flex justifyContent="space-between" alignItems="center">
          <RequiredFieldsCounter sections={config.sections} />
          <FormFooter
            onCancel={onCancel}
            dirtyRef={dirtyRef}
            submitButtonText={submitButtonText}
            cancelButtonText={cancelButtonText}
          />
        </Flex>
      </Flex>
    </Form>
  );
};
